import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import { getUserDisplayName } from '../utils/userUtils';
import './Dashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || '/lb-api';

// Range keys map to the backend's comparison windows.
const RANGE_OPTIONS = [
  { key: 'week', label: 'Last Week' },
  { key: 'month', label: 'Last Month' },
  { key: 'quarter', label: 'Last 3 Months' },
  { key: '6months', label: 'Last 6 Months' },
];

// Format an ISO timestamp into "Today at 09:42am" / "21 Jul at 09:42am".
function formatLastActive(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase().replace(' ', '');
  const sameDay = d.toDateString() === now.toDateString();
  const yst = new Date(now); yst.setDate(now.getDate() - 1);
  const isYst = d.toDateString() === yst.toDateString();
  if (sameDay) return `Today at ${time}`;
  if (isYst) return `Yesterday at ${time}`;
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} at ${time}`;
}

// Format an ISO date into "21st July 2026".
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const day = d.getDate();
  const j = day % 10, k = day % 100;
  const suffix = (j === 1 && k !== 11) ? 'st' : (j === 2 && k !== 12) ? 'nd' : (j === 3 && k !== 13) ? 'rd' : 'th';
  return `${day}${suffix} ${d.toLocaleDateString('en-GB', { month: 'long' })} ${d.getFullYear()}`;
}

// Tint a black SVG (from /public/images) to any color using CSS mask. Applied via
// inline style so CRA's css-loader doesn't try to resolve the public asset path.
const maskStyle = (src, color) => ({
  backgroundColor: color,
  WebkitMaskImage: `url(${src})`,
  maskImage: `url(${src})`,
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
});

// Empty-state block shown when a section has no data for the selected period.
// Dashed circle with a line-chart icon, a "Yet to Start" heading and a subline.
const YetToStart = ({ text }) => (
  <div className="db-yts">
    <div className="db-yts-icon">
      <img className="db-yts-ring" src="/images/circledashed.svg" alt="" />
      <img className="db-yts-chart" src="/images/keeplearningicon.svg" alt="" />
    </div>
    <div className="db-yts-body">
      <div className="db-yts-title">Yet to Start</div>
      <div className="db-yts-sub">{text}</div>
    </div>
  </div>
);

// Donut with proportional segments (sized by time), a faint base ring, dashed
// leader lines, and a center total. Labels are anchored to fixed columns on the
// left/right so they never collide even when one skill dominates the total.
const Donut = ({ skills, centerTop, centerBottom }) => {
  // Canvas has dedicated left/right gutters for labels so text never overlaps the
  // ring. The ring is centered; labels live entirely in the gutters.
  const W = 560, H = 360;            // full canvas incl. label gutters
  const cx = 280, cy = 180;          // ring center
  const TAU = 2 * Math.PI;

  const rawTotal = skills.reduce((sum, s) => sum + (s.minutes || 0), 0) || 1;
  const maxMin = Math.max(...skills.map(s => s.minutes || 0), 1);

  // Each segment's THICKNESS varies with its time: most-time = thickest.
  const MIN_W = 10, MAX_W = 22;
  const widthFor = (m) => MIN_W + (MAX_W - MIN_W) * ((m || 0) / maxMin);
  const innerEdge = 82;              // fixed inner boundary all segments touch
  const BASE_W = MAX_W + 6;          // base band a touch wider than thickest segment
  const baseR = innerEdge + BASE_W / 2;
  const outerR = baseR + BASE_W / 2; // outer edge of the base band

  // Give every segment a minimum visible slice so tiny skills don't vanish, while
  // keeping the true proportions for the bulk of the ring.
  const MIN_FRAC = 0.05;             // 5% minimum arc per segment
  const rawFracs = skills.map(s => (s.minutes || 0) / rawTotal);
  const floored = rawFracs.map(f => Math.max(f, MIN_FRAC));
  const flooredSum = floored.reduce((a, b) => a + b, 0);
  const fracs = floored.map(f => f / flooredSum); // renormalised to sum to 1

  // Precompute each segment's start/end fraction, thickness and true midpoint angle.
  let acc = 0;
  const segs = skills.map((s, i) => {
    const frac = fracs[i];
    const start = acc; acc += frac;
    const midFrac = start + frac / 2;
    const ang = midFrac * TAU - Math.PI / 2; // 0 at top, clockwise
    const w = widthFor(s.minutes);
    const rr = innerEdge + w / 2;    // radius so this segment's INNER edge = innerEdge
    return { ...s, frac, start, ang, w, rr };
  });

  const pt = (ang, radius) => ({ x: cx + radius * Math.cos(ang), y: cy + radius * Math.sin(ang) });

  // Arc path for a segment (start->end fraction) at the given radius.
  const arcPath = (startFrac, endFrac, radius) => {
    const a0 = startFrac * TAU - Math.PI / 2;
    const a1 = endFrac * TAU - Math.PI / 2;
    const gap = 0.012 * TAU; // tiny gap between segments (like Figma)
    const s = a0 + gap / 2, e = a1 - gap / 2;
    const p0 = pt(s, radius), p1 = pt(e, radius);
    const large = (e - s) > Math.PI ? 1 : 0;
    return `M ${p0.x} ${p0.y} A ${radius} ${radius} 0 ${large} 1 ${p1.x} ${p1.y}`;
  };

  // LABEL LAYOUT (responsive & overlap-proof):
  //  - Each label goes on the SAME side as its segment (by angle), so the leader
  //    line points at the correct slice.
  //  - Within each side, labels are distributed EVENLY down the full canvas height
  //    (ordered by angle). This guarantees no overlap for any time distribution and
  //    keeps text in the side gutters, never over the ring.
  const LABEL_R = outerR + 10;                 // where the radial stub ends
  const angleFromTop = (a) => (a + Math.PI / 2 + 2 * Math.PI) % (2 * Math.PI); // 0..2PI clockwise from top
  const sided = segs.map((s) => ({ s, isRight: Math.cos(s.ang) >= 0, a: angleFromTop(s.ang) }));

  // Right side: order top->bottom by angle (0 at top increasing clockwise).
  // Left side: angles are on the far side; order so the topmost (near 2PI) is first.
  const rightArr = sided.filter(x => x.isRight).sort((p, q) => p.a - q.a);
  const leftArr = sided.filter(x => !x.isRight).sort((p, q) => q.a - p.a);

  const TOP = 30, BOTTOM = H - 30;             // vertical band the labels occupy
  const slotYFor = (idx, count) => {
    if (count <= 1) return cy;                 // single label sits at center height
    return TOP + (idx * (BOTTOM - TOP)) / (count - 1);
  };
  const slotMap = new Map();
  rightArr.forEach((x, i) => slotMap.set(x.s, { isRight: true, slotY: slotYFor(i, rightArr.length) }));
  leftArr.forEach((x, i) => slotMap.set(x.s, { isRight: false, slotY: slotYFor(i, leftArr.length) }));

  const labelInfos = segs.map((s) => {
    const r = slotMap.get(s);
    return {
      s,
      isRight: r.isRight,
      slotY: r.slotY,
      elbowX: r.isRight ? W - 150 : 150,       // where the horizontal run begins
      colX: r.isRight ? W - 134 : 134,         // text column x (inside the gutter)
    };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="db-donut">
      {/* lilac base track under the colored segments */}
      <circle cx={cx} cy={cy} r={baseR} fill="none" stroke="#D9C7FA" strokeWidth={BASE_W} />
      {/* proportional colored segments — thickness varies by time, all touch the
          same inner edge (innerEdge) and grow outward. */}
      {segs.map((s, i) => (
        <path key={i} d={arcPath(s.start, s.start + s.frac, s.rr)} fill="none"
          stroke={s.color} strokeWidth={s.w} strokeLinecap="butt" />
      ))}
      {/* center total (extra gap between the hours and the caption below it) */}
      <text x={cx} y={cy - 6} className="db-donut-center-top" textAnchor="middle">{centerTop}</text>
      <text x={cx} y={cy + 22} className="db-donut-center-bottom" textAnchor="middle">{centerBottom}</text>

      {/* leader lines + labels: segment midpoint -> short radial stub -> elbow at
          the label's row -> horizontal run into the gutter label. */}
      {labelInfos.map(({ s, isRight, slotY, colX, elbowX }, i) => {
        const p1 = pt(s.ang, outerR + 2);            // on the ring, at the segment's midpoint
        const p2 = pt(s.ang, LABEL_R);               // short radial stub outward
        const p3 = { x: elbowX, y: slotY };          // elbow aligned to the evenly-distributed label row
        const p4 = { x: colX, y: slotY };            // horizontal run into the label column
        const anchor = isRight ? 'start' : 'end';
        const textX = isRight ? colX + 4 : colX - 4;
        return (
          <g key={'l' + i}>
            <polyline points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y} ${p4.x},${p4.y}`}
              fill="none" stroke="#9A9A9A" strokeWidth="1.2" strokeDasharray="3 3" />
            <text x={textX} y={slotY - 3} textAnchor={anchor} className="db-donut-lbl-name">{s.name}</text>
            <text x={textX} y={slotY + 14} textAnchor={anchor} className="db-donut-lbl-val" fill={s.color}>{s.label}</text>
          </g>
        );
      })}
    </svg>
  );
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // When returning from a list page, restore the time-period the user had selected
  // before they left (passed back via location.state.range). Otherwise default 6months.
  const VALID_RANGES = ['week', 'month', 'quarter', '6months'];
  const incomingRange = location.state && VALID_RANGES.includes(location.state.range) ? location.state.range : '6months';
  const [range, setRange] = useState(incomingRange);
  const [activeLang, setActiveLang] = useState('all');
  const [banner, setBanner] = useState(null);     // fetched banner metrics
  const [progress, setProgress] = useState(null); // fetched "progress so far" metrics
  const [practice, setPractice] = useState(null); // fetched Practice section
  const [assessments, setAssessments] = useState(null); // fetched Assessments section
  const [learningProgress, setLearningProgress] = useState(null); // fetched Learning Progress cards
  const [availableLanguages, setAvailableLanguages] = useState(null); // language tabs from API
  const [continueJourney, setContinueJourney] = useState(null); // fetched "continue" recommendation
  const [cultural, setCultural] = useState(null); // fetched Cultural Snapshot

  // Track time spent on the dashboard page (logged to user_page_logs as 'dashboard').
  usePageTimeTracker('dashboard', user);

  // When returning from a summary page via its "Back" button, scroll to the section
  // the user came from (e.g. Assessments). The target renders after data loads, so
  // wait for `assessments` before scrolling.
  const scrollTarget = location.state && location.state.scrollTo;
  useEffect(() => {
    if (!scrollTarget) return;
    const id = scrollTarget === 'assessments' ? 'db-section-assessments' : null;
    if (!id) return;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Clear the state so a later refresh doesn't re-scroll.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [scrollTarget, assessments, navigate, location.pathname]);

  // Base (all-language) sections — refetch ONLY when the time period changes,
  // NOT when the language tab changes: banner, "progress so far", Cultural
  // Snapshot, Continue band (overall latest), and the available-language tabs.
  // Always requested with language=all since these are not language-scoped.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/dashboard/summary?range=${range}&language=all&_t=${Date.now()}`, { withCredentials: true })
      .then(res => {
        if (cancelled || !res.data || !res.data.success) return;
        setBanner(res.data.banner);
        setProgress(res.data.progress);
        setCultural(res.data.cultural);
        setContinueJourney(res.data.continueJourney);
        if (Array.isArray(res.data.availableLanguages)) setAvailableLanguages(res.data.availableLanguages);
      })
      .catch(() => {
        if (!cancelled) { setBanner(null); setProgress(null); setCultural(null); setContinueJourney(null); }
      });
    return () => { cancelled = true; };
  }, [range]);

  // Language-aware sections — refetch when EITHER the time period OR the language
  // tab changes: Learning Progress, Practice (Language Skill + Business Scenario),
  // and Assessments (Post-Assessments + Exam Prep).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/dashboard/summary?range=${range}&language=${activeLang}&_t=${Date.now()}`, { withCredentials: true })
      .then(res => {
        if (cancelled || !res.data || !res.data.success) return;
        setPractice(res.data.practice);
        setAssessments(res.data.assessments);
        setLearningProgress(res.data.learningProgress);
      })
      .catch(() => {
        if (!cancelled) { setPractice(null); setAssessments(null); setLearningProgress(null); }
      });
    return () => { cancelled = true; };
  }, [range, activeLang]);
  const displayName = getUserDisplayName(user) || 'Learner';
  const initial = displayName.slice(0, 1).toUpperCase();
  // Word used in the "+Xh learning this ___" note lines, driven by the range.
  const periodWord = range === 'week' ? 'week' : range === 'month' ? 'month' : range === 'quarter' ? 'quarter' : '6 months';

  // Learning Progress cards from the API (fallback to none until loaded), filtered
  // by the active language tab.
  const lpCards = (learningProgress || []).map(c => ({
    lang: c.language,
    flag: c.flag,
    level: c.levelLabel || c.level || '',
    current: c.currentChapter || null,          // null => not started
    started: !!c.currentChapter,
    completed: `${c.chaptersDone}/${c.chaptersTotal}`,
    pct: c.pct,
  }));
  // Server already scopes by the active language, so no client-side filter needed.
  const visibleProgress = lpCards;

  // Language tabs come from the API (only languages with data), with an "All" tab.
  const langTabs = (availableLanguages && availableLanguages.length)
    ? availableLanguages.map(l => ({
        key: l.code,
        label: l.code === 'all' ? 'All Languages' : l.name,
        flag: l.code === 'all' ? null : `/images/flag-${(l.name || '').toLowerCase()}.svg`,
      }))
    : [{ key: 'all', label: 'All Languages', flag: null }];

  // Practice: derive donut skills (minutes drive the arc), legend, skill counts,
  // and business scenario from the fetched `practice` (fallback to placeholder).
  const SKILL_ICON = {
    Reading: '/images/Group.svg', Listening: '/images/readwithoutcolor.svg',
    Writing: '/images/Draw.svg', Speaking: '/images/blurrmic.svg',
  };
  const pSkills = practice?.languageSkill?.skills || [];
  // Round each skill's time to whole minutes ONCE and reuse that value everywhere
  // (donut arc, legend label, and the center total). Deriving the total from the
  // same rounded per-skill minutes guarantees the center total always equals the
  // sum of the displayed parts — avoiding the off-by-a-minute mismatch that comes
  // from rounding each skill and the raw total independently.
  const fmtMinutes = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const skillMinutes = pSkills.map(s => Math.round((s.seconds || 0) / 60));
  const donutSkills = pSkills.map((s, i) => ({ name: s.name, label: fmtMinutes(skillMinutes[i]), minutes: skillMinutes[i], color: s.color }));
  const skillCounts = pSkills.map(s => ({ name: s.name, value: s.count, color: s.color, icon: SKILL_ICON[s.name] }));
  const totalMinutes = skillMinutes.reduce((a, b) => a + b, 0);
  const totalHoursLabel = fmtMinutes(totalMinutes);
  const biz = practice?.businessScenario || null;
  // Per-section empty states for a selected language with no data.
  const practiceEmpty = practice && practice.hasData === false;
  const assessEmpty = assessments && assessments.hasData === false;
  const ap = assessments?.post || null;
  const ae = assessments?.exam || null;

  // Continue Your Journey band (falls back to a generic prompt when no learn data).
  const cj = continueJourney || null;
  const cjLang = cj?.hasData ? cj.language : null;
  const cjResumeLabel = cj?.hasData && cj.chapterNumber
    ? `Resume Chapter ${cj.chapterNumber}`
    : (cj?.hasData ? 'Resume Learning' : 'Start Learning');

  // Build the Learn-page navigation state from the recommendation so Resume opens
  // the correct language + level. Learn expects a language CODE ('fr') and a
  // proficiencyLevel SLUG ('elementary-a2').
  const NAME_TO_CODE_CJ = { english: 'en', french: 'fr', german: 'de', spanish: 'es', italian: 'it', japanese: 'ja' };
  const LEVEL_TO_SLUG = {
    A1: 'beginner-a1', A2: 'elementary-a2', B1: 'pre-intermediate-b1', B2: 'intermediate-b2',
    C1: 'advanced-c1', C2: 'proficient-c2',
    N5: 'beginner-n5', N4: 'pre-intermediate-n4', N3: 'intermediate-n3', N2: 'advanced-n2', N1: 'proficient-n1',
  };
  const cjNavState = cj?.hasData ? {
    language: NAME_TO_CODE_CJ[String(cj.language || '').toLowerCase()] || 'de',
    proficiencyLevel: LEVEL_TO_SLUG[String(cj.level || '').toUpperCase()] || 'beginner-a1',
    // The chapter to resume — Learn pre-selects this chapter and shows its prompts.
    resumeChapter: cj.chapterNumber || null,
  } : {};

  // Cultural Snapshot from the API (fallback to placeholder until loaded).
  const cul = cultural || null;
  const culCountries = cul?.countries || [];

  // Horizontal scroll via CircleArrow left/right buttons (no visible scrollbar).
  // Each row tracks whether it can still scroll left/right so the arrows hide at the ends.
  const progressRowRef = useRef(null);
  const countriesRowRef = useRef(null);
  const [lpScroll, setLpScroll] = useState({ left: false, right: false });
  const [culScroll, setCulScroll] = useState({ left: false, right: false });
  const scrollRow = (ref, dir = 1) => { if (ref.current) ref.current.scrollBy({ left: 340 * dir, behavior: 'smooth' }); };
  const updateArrows = (ref, setState) => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft < (el.scrollWidth - el.clientWidth - 1);
    setState(prev => (prev.left === left && prev.right === right ? prev : { left, right }));
  };
  // Recompute arrow visibility when the number of cards in each row changes.
  useEffect(() => { updateArrows(progressRowRef, setLpScroll); }, [visibleProgress.length]);
  useEffect(() => { updateArrows(countriesRowRef, setCulScroll); }, [culCountries.length]);

  return (
    <div className="db-page">
      <Header user={user} />

      {/* Shared page header: hamburger (SideNav) + breadcrumb, no language/level dropdowns. */}
      <div className="nav-header-container">
        <SideNav />
        <PageHeader
          breadcrumbs={[
            { label: 'Home', path: '/', current: false },
            { label: 'Dashboard', current: true },
          ]}
          rightContent={<></>}
        />
      </div>

      <main className="db-main">
        {/* Title + range */}
        <div className="db-title-row">
          <div>
            <h1 className="db-title">My Dashboard</h1>
            <p className="db-subtitle">Learner Summary</p>
          </div>
          <select value={range} onChange={(e) => setRange(e.target.value)} className="db-range-select">
            {RANGE_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>

        {/* User banner */}
        <div className="db-banner">
          <div className="db-banner-left">
            <div className="db-banner-avatar">{initial}<span className="db-banner-dot" /></div>
            <div>
              <div className="db-banner-name">{displayName}</div>
              <div className="db-banner-caption">Last Active: {formatLastActive(banner?.lastActive)}</div>
            </div>
          </div>
          <div className="db-banner-chips">
            <span className="db-chip db-chip-green"><i className="db-chip-ico" style={maskStyle('/images/Strategy.svg', '#167C61')} /><span><strong>{banner ? `${banner.streakDays} - day` : '—'}</strong> Streak</span></span>
            {banner?.compare && (
              <span className="db-chip db-chip-blue">
                <i className="db-chip-ico" style={maskStyle(banner.moreDeltaHours < 0 ? '/images/degradedlevel.svg' : '/images/upgradedlevel.svg', '#1D4ED8')} />
                <span><strong>{(banner.moreDeltaHours >= 0 ? '+' : '') + banner.moreDeltaHours}h</strong> {banner.moreDeltaHours < 0 ? 'less' : 'more'} this {range === 'week' ? 'week' : range === 'month' ? 'month' : 'period'}</span>
              </span>
            )}
            <span className="db-chip db-chip-lilac"><i className="db-chip-ico" style={maskStyle('/images/Info.svg', '#7500C0')} /><span><strong>{banner ? `${banner.totalLearningHours}h` : '—'}</strong> - Total Learning Time</span></span>
          </div>
        </div>

        {/* Progress so far */}
        <section className="db-section db-progress-wrap">
          <h2 className="db-section-title">Your progress so far</h2>
          <p className="db-section-sub">Continue to learn more about different languages and cultures.</p>
          <div className="db-stat-grid">
            <div className="db-stat-card">
              <div className="db-stat-label">Learning Lesson</div>
              <div className="db-stat-value">{progress ? progress.learningLessons : '—'}</div>
              <div className="db-stat-caption">Chapters completed across all languages</div>
              {progress?.compareTime && progress?.learnDeltaHours != null && (
                <div className="db-stat-note">
                  {(progress.learnDeltaHours >= 0 ? '+' : '') + progress.learnDeltaHours}h {progress.learnDeltaHours < 0 ? 'less' : 'more'} learning this {periodWord}
                </div>
              )}
            </div>
            <div className="db-stat-card">
              <div className="db-stat-label">Language Skill</div>
              <div className="db-stat-value">{progress ? progress.languageSkill : '—'}</div>
              <div className="db-stat-caption">Prompt/Activities of RLWS covered till now</div>
              {progress?.compareTime && progress?.practiceDeltaHours != null && (
                <div className="db-stat-note">
                  {(progress.practiceDeltaHours >= 0 ? '+' : '') + progress.practiceDeltaHours}h {progress.practiceDeltaHours < 0 ? 'less' : 'more'} practice this {periodWord}
                </div>
              )}
            </div>
            <div className="db-stat-card">
              <div className="db-stat-label">Business Scenario Practiced</div>
              <div className="db-stat-value">{progress ? progress.businessScenario.done : '—'}<span className="db-stat-total"> / {progress ? progress.businessScenario.total : '—'}</span></div>
              <div className="db-stat-caption">Across all languages</div>
              {progress?.businessScenario.avgScore != null && (
                <div className="db-stat-note">Average score- {progress.businessScenario.avgScore}%</div>
              )}
            </div>
            <div className="db-stat-card">
              <div className="db-stat-label">Assessment Attempted</div>
              <div className="db-stat-split">
                <div>
                  <div className="db-stat-value db-stat-value-sm">{progress ? progress.assessment.post : '—'}</div>
                  <div className="db-stat-caption">Post Assessments</div>
                  {progress?.assessment.highestLevel && (
                    <div className="db-stat-note">Highest Prof: {progress.assessment.highestLevel} in {progress.assessment.highestLevelLang}</div>
                  )}
                </div>
                <div className="db-stat-divider" />
                <div>
                  <div className="db-stat-value db-stat-value-sm">{progress ? progress.assessment.exam : '—'}</div>
                  <div className="db-stat-caption">Exam mock-up</div>
                  {progress?.assessment.examAvgPct != null && (
                    <div className="db-stat-note">Avg Score - {progress.assessment.examAvgPct}%</div>
                  )}
                </div>
              </div>
            </div>
            <div className="db-stat-card">
              <div className="db-stat-label">Cultural Explored</div>
              <div className="db-stat-split">
                <div>
                  <div className="db-stat-value db-stat-value-sm">{progress ? progress.cultural.countries : '—'}<span className="db-stat-total"> / {progress ? progress.cultural.countriesTotal : '—'}</span></div>
                  <div className="db-stat-caption">Countries Explored</div>
                </div>
                <div className="db-stat-divider" />
                <div>
                  <div className="db-stat-value db-stat-value-sm">{progress ? progress.cultural.challenges : '—'}</div>
                  <div className="db-stat-caption">Challenges Completed</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Language Snapshot */}
        <section className="db-section db-snapshot-section">
          <h2 className="db-snapshot-title">Your Language Snapshot</h2>
          <p className="db-snapshot-sub">A glimpse into your language learning journey</p>

          {/* Language tabs */}
          <div className="db-tabs">
            {langTabs.map(l => (
              <button key={l.key}
                className={'db-tab' + (activeLang === l.key ? ' active' : '')}
                onClick={() => setActiveLang(l.key)}>
                {l.flag && <img src={l.flag} alt="" width="18" height="18" onError={e => { e.target.style.display = 'none'; }} />}
                {l.label}
              </button>
            ))}
          </div>

          <div className="db-snapshot-cards">
          {/* Learning Progress */}
          <div className="db-card">
            <div className="db-card-head">
              <h3 className="db-card-title">Learning Progress</h3>
              <p className="db-card-sub">Chapters completed and your current progress across your attempted languages</p>
            </div>
            <div className="db-scroll-wrap">
              <div className="db-progress-row" ref={progressRowRef} onScroll={() => updateArrows(progressRowRef, setLpScroll)}>
                {visibleProgress.length === 0 ? (
                  <YetToStart text="No learning data yet. Your chapter progress across languages will appear once you start learning." />
                ) : visibleProgress.map((p, i) => (
                  <div key={i} className="db-lang-card">
                    <div className="db-lang-card-top">
                      <img src={p.flag} alt="" width="22" height="22" onError={e => { e.target.style.display = 'none'; }} />
                      <span className={'db-level-chip' + (p.pct === 0 ? ' muted' : '')}>{p.level}</span>
                    </div>
                    <div className="db-lang-name">{p.lang}</div>
                    <div className="db-lang-current">
                      {p.started
                        ? <>Current: <strong>{p.current}</strong></>
                        : 'Begin when you are ready'}
                    </div>
                    <div className="db-lang-bar"><div className="db-lang-bar-fill" style={{ width: p.pct + '%' }} /></div>
                    <div className="db-lang-foot">
                      <span>Chapters Completed</span>
                      <span className="db-lang-foot-val">{p.completed}</span>
                    </div>
                  </div>
                ))}
              </div>
              {lpScroll.left && (
                <button className="db-scroll-arrow db-scroll-arrow-left" onClick={() => scrollRow(progressRowRef, -1)} aria-label="Scroll left">
                  <img src="/images/CircleArrowleft.svg" alt="" width="18" height="18" />
                </button>
              )}
              {lpScroll.right && (
                <button className="db-scroll-arrow db-scroll-arrow-right" onClick={() => scrollRow(progressRowRef, 1)} aria-label="Scroll right">
                  <img src="/images/CircleArrowright.svg" alt="" width="18" height="18" />
                </button>
              )}
            </div>
          </div>

          {/* Practice */}
          <div className="db-card">
            <div className="db-card-head">
              <h3 className="db-card-title">Practice</h3>
              <p className="db-card-sub">Skills completed and your current progress across your attempted languages</p>
            </div>
            {practiceEmpty ? (
              <YetToStart text="No practice data yet. Your Reading, Listening, Writing, and Speaking activity chart will appear once you start practicing." />
            ) : (
            <div className="db-practice-grid">
              {/* LEFT: Language Skill */}
              <div className="db-practice-left">
                <div className="db-subhead">Language Skill</div>
                <div className="db-subhead-sub">Activities completed across all languages</div>
                <div className="db-donut-block">
                  <Donut skills={donutSkills} centerTop={totalHoursLabel} centerBottom="Total time given" />
                  <div className="db-donut-legend">
                    {[...donutSkills, { name: 'Total time spend', color: '#E6DCFF' }].map((sk, i) => (
                      <div key={i} className="db-legend-row">
                        <span className="db-legend-dot" style={{ background: sk.color }} />
                        <span className="db-legend-name">{sk.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="db-skill-counts">
                  {skillCounts.map((sc, i) => (
                    <div key={i} className="db-skill-count">
                      <img className="db-skill-count-ico" src={sc.icon} alt="" width="11" height="11" onError={e => { e.target.style.display = 'none'; }} />
                      <div className="db-skill-count-name">{sc.name}</div>
                      <div className="db-skill-count-val" style={{ color: sc.color }}>{sc.value}</div>
                      <div className="db-skill-count-cap">Prompts explored</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT: Business Scenario */}
              <div className="db-practice-right">
                <div className="db-subhead">Business Scenario</div>
                <div className="db-subhead-sub">Real world conversation practice across all languages</div>
                <div className="db-biz-cap">Completed scenario across all languages</div>
                <div className="db-biz-score">{biz ? biz.done : '—'}<span className="db-biz-total"> / {biz ? biz.total : '—'}</span></div>
                <div className="db-biz-across">Across all languages</div>
                {biz?.avgScore != null && (
                  <div className="db-biz-avg">Average conversation score - {biz.avgScore}%</div>
                )}
                {biz?.latest && (
                  <div className="db-biz-latest">
                    <div className="db-biz-latest-label">Latest</div>
                    <div className="db-biz-latest-row">
                      <span>{biz.latest.title}{biz.latest.language ? ` - ${biz.latest.language}` : ''}</span>
                      <span className="db-biz-latest-status" style={{ color: biz.latest.status === 'Completed' ? '#065F46' : '#C16B02' }}>{biz.latest.status}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Assessments */}
          <div className="db-card" id="db-section-assessments">
            <div className="db-card-head">
              <h3 className="db-card-title">Assessments</h3>
              <p className="db-card-sub">An overview of your results</p>
            </div>
            {assessEmpty ? (
              <YetToStart text="No assessment data yet. Your post-assessment and exam prep results will appear once you complete an assessment." />
            ) : (
            <div className="db-assess-grid">
              <div className="db-assess-col">
                <div className="db-subhead">Post - Assessments</div>
                <div className="db-subhead-sub">Your proficiency across languages</div>
                <div className="db-assess-stats">
                  <div><div className="db-assess-num">{ap ? ap.attempts : '—'}</div><div className="db-assess-cap">Assessments Completed</div></div>
                  <div className="db-assess-divider" />
                  <div><div className="db-assess-num">{ap && ap.highestLevel ? ap.highestLevel : '—'}</div><div className="db-assess-cap">Highest Proficiency level{ap && ap.highestLevelLang ? <> - <strong>{ap.highestLevelLang}</strong></> : ''}</div></div>
                </div>
                {ap && ap.recentLevel && (
                  <span className="db-tag db-tag-green"><img className="db-tag-ico" src="/images/AssementLeadingIcon.svg" alt="" width="14" height="14" />Recent - {ap.recentLang} {ap.recentLevel}</span>
                )}
                {ap && ap.lastAssessed && (
                  <div className="db-assess-note"><img className="db-note-ico" src="/images/Info.svg" alt="" width="13" height="13" />{ap.recentLang} Last Assessed - {formatDate(ap.lastAssessed)}</div>
                )}
                <button className="db-btn db-btn-outline" onClick={() => navigate('/assessment-list', { state: { lang: activeLang, range } })}>View Assessment Summary <img className="db-btn-arrow" src="/images/arrow-right.svg" alt="" width="14" height="14" /></button>
              </div>
              <div className="db-assess-vdivider" />
              <div className="db-assess-col">
                <div className="db-subhead">Exam Prep</div>
                <div className="db-subhead-sub">Mock test completed and your current progress across your attempted languages</div>
                <div className="db-assess-stats">
                  <div><div className="db-assess-num">{ae ? ae.attempts : '—'}</div><div className="db-assess-cap">Exams Completed</div></div>
                  <div className="db-assess-divider" />
                  <div><div className="db-assess-num">{ae && ae.highestPct != null ? `${ae.highestPct}%` : '—'}</div><div className="db-assess-cap">Highest score{ae && ae.highestLang ? <> - <strong>{ae.highestLang}</strong></> : ''}</div></div>
                </div>
                <div className="db-tag db-tag-green db-tag-lead"><img className="db-tag-ico" src="/images/AssementLeadingIcon.svg" alt="" width="14" height="14" /><span>2 attempts allowed per 24 hours. You can retake the exam up to twice within any 24-hour period.</span></div>
                <div className="db-tag db-tag-amber db-tag-lead"><img className="db-tag-ico" src="/images/Info.svg" alt="" width="14" height="14" /><span><strong>Incomplete attempts are not counted.</strong><br /> Exiting before submitting and generating a result will not use up an attempt.</span></div>
                <button className="db-btn db-btn-outline" onClick={() => navigate('/exam-prep-list', { state: { lang: activeLang, range } })}>View Exam Prep Summary <img className="db-btn-arrow" src="/images/arrow-right.svg" alt="" width="14" height="14" /></button>
              </div>
            </div>
            )}
          </div>
          </div>
        </section>

        {/* Continue Journey band */}
        <section className="db-continue-band">
          <div>
            <div className="db-continue-eyebrow">Recommended next action</div>
            <div className="db-continue-title">{cjLang ? `Continue Your ${cjLang} Journey` : 'Start Your Learning Journey'}</div>
            <div className="db-continue-sub">{cjLang ? 'Pick up where you left off and keep learning' : 'Choose a language and begin your first chapter'}</div>
          </div>
          <div className="db-continue-actions">
            <button className="db-btn db-btn-outline" onClick={() => navigate('/explore-learning', { state: cjNavState })}>Explore Learning</button>
            <button className="db-btn db-btn-primary" onClick={() => navigate('/learn', { state: cjNavState })}>{cjResumeLabel} <i className="db-btn-arrow-white" style={maskStyle('/images/arrow-right.svg', '#FFFFFF')} /></button>
          </div>
        </section>

        {/* Cultural Snapshot */}
        <section className="db-section db-snapshot-section">
          <h2 className="db-snapshot-title">Your Cultural Snapshot</h2>
          <p className="db-snapshot-sub">A look at your cultural learning</p>
          <div className="db-cultural-card">
            <div className="db-card db-cultural-inner">
            <div className="db-cultural-stats">
              <div><div className="db-cultural-num">{cul ? cul.countriesExplored : '—'}<span className="db-cultural-total">/{cul ? cul.countriesTotal : '—'}</span></div><div className="db-cultural-cap">Countries Explored</div></div>
              <div><div className="db-cultural-num">{cul ? cul.challengesCompleted : '—'}</div><div className="db-cultural-cap">Challenges Completed</div></div>
            </div>
            <div className="db-cultural-divider" />
            <div className="db-subhead db-cultural-subhead">Countries Explored</div>
            {culCountries.length === 0 ? (
              <YetToStart text="No cultural data yet. The countries you explore will appear here once you start exploring." />
            ) : (
            <div className="db-scroll-wrap">
              <div className="db-cultural-grid" ref={countriesRowRef} onScroll={() => updateArrows(countriesRowRef, setCulScroll)}>
                {culCountries.map((c, i) => (
                  <div key={i} className="db-country-card">
                    <img src={c.flag} alt="" width="18" height="18" onError={e => { e.target.style.display = 'none'; }} />
                    <div className="db-country-name">{c.country}</div>
                    <div className="db-country-meta"><strong>{c.topicsCovered}</strong>"/"{c.topicsTotal} Topics Covered</div>
                    <div className="db-country-meta"><strong>{c.challengesAttempted}</strong> Challenges Attempted</div>
                  </div>
                ))}
              </div>
              {culScroll.left && (
                <button className="db-scroll-arrow db-scroll-arrow-left" onClick={() => scrollRow(countriesRowRef, -1)} aria-label="Scroll left">
                  <img src="/images/CircleArrowleft.svg" alt="" width="18" height="18" />
                </button>
              )}
              {culScroll.right && (
                <button className="db-scroll-arrow db-scroll-arrow-right" onClick={() => scrollRow(countriesRowRef, 1)} aria-label="Scroll right">
                  <img src="/images/CircleArrowright.svg" alt="" width="18" height="18" />
                </button>
              )}
            </div>
            )}
            </div>
            {cul?.latest && (
              <div className="db-cultural-foot">
                <div>
                  <span className="db-tag db-tag-green"><strong>Latest Explored :</strong> {cul.latest.country}{cul.latest.category ? ` - ${cul.latest.category}` : ''}</span>
                  <div className="db-assess-note">{cul.latest.country}{cul.latest.category ? ` - ${cul.latest.category}` : ''} - Visited on {formatDate(cul.latest.completedAt)}</div>
                </div>
                <div className="db-cultural-actions">
                  <button className="db-btn db-btn-outline" onClick={() => navigate('/cultural-insights')}>Explore Cultural Insights →</button>
                  <button className="db-btn db-btn-primary" onClick={() => navigate('/cultural-insights-challenge')}>Take up a Challenge →</button>
                </div>
              </div>
            )}
            {!cul?.latest && (
              <div className="db-cultural-foot">
                <div />
                <div className="db-cultural-actions">
                  <button className="db-btn db-btn-outline" onClick={() => navigate('/cultural-insights')}>Explore Cultural Insights →</button>
                  <button className="db-btn db-btn-primary" onClick={() => navigate('/cultural-insights-challenge')}>Take up a Challenge →</button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
